/**
 * Admin WebSocket client — connects to backend WSS gateway for realtime push.
 *
 * Backend contract (`hm-location-v1`):
 * - URL: VITE_WS_URL — prod `wss://hm.back.escroud.com/ws`, local often `ws://hmback/ws`
 * - After open / hello, first frame must be auth, e.g.
 *   `{ "type": "auth", "role": "technician", "token": "<uid>", "task_type": "order", "task_id": 171 }`
 *   Admin board: `{ "type": "auth", "role": "admin", "token": "<jwt>" }`
 * - Heartbeat: client `{ type: "ping" }`; server may reply `pong`
 * - Location: `auth_ok.location` and/or `{ type: "location", latitude, longitude, ... }`
 */

export type WsMessageHandler = (message: WsIncomingMessage) => void

export interface WsIncomingMessage {
  type: string
  ts?: number
  data?: unknown
  raw: unknown
}

export type WsConnectionStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'no-token'

/** First-frame auth payload after socket open (and for later re-auth / track switch). */
export type WsAuthPayload = {
  type: 'auth'
  role: string
  token: string
  task_type?: string
  task_id?: number | string
}

type WsClientOptions = {
  url?: string
  getToken?: () => string | null
  /** Backend auth role — Admin site default `admin`; location track uses `technician` / `inspector`. */
  role?: string
  /** Optional tracking fields merged into auth when using default payload builder. */
  getTaskType?: () => string | undefined
  getTaskId?: () => number | string | undefined
  /** Full control over auth frame; return null to skip connect / reauth. */
  getAuthPayload?: () => WsAuthPayload | null
  /** Rooms/channels to request after auth (backend may ignore if auto-joined by role). */
  subscribeRooms?: string[]
  heartbeatMs?: number
  maxReconnectMs?: number
  onStatus?: (status: WsConnectionStatus) => void
}

const DEFAULT_WS_URL = 'wss://hm.back.escroud.com/ws'

function normalizeIncoming(raw: unknown): WsIncomingMessage | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const text = raw.trim()
    if (!text) return null
    if (text === 'pong' || text === 'ping') {
      return { type: text, raw }
    }
    try {
      return normalizeIncoming(JSON.parse(text))
    } catch {
      return { type: 'message', data: text, raw }
    }
  }
  if (typeof raw !== 'object') {
    return { type: 'message', data: raw, raw }
  }

  const row = raw as Record<string, unknown>
  const type =
    (typeof row.type === 'string' && row.type) ||
    (typeof row.event === 'string' && row.event) ||
    (typeof row.action === 'string' && row.action) ||
    'message'
  const data = row.data ?? row.payload ?? row.body ?? row
  const ts = typeof row.ts === 'number' ? row.ts : undefined
  return { type, ts, data, raw }
}

export class WsClient {
  private readonly url: string
  private readonly getToken: () => string | null
  private readonly role: string
  private readonly getTaskType?: () => string | undefined
  private readonly getTaskId?: () => number | string | undefined
  private readonly getAuthPayloadCustom?: () => WsAuthPayload | null
  private readonly subscribeRooms: string[]
  private readonly heartbeatMs: number
  private readonly maxReconnectMs: number
  private readonly onStatus?: (status: WsConnectionStatus) => void

  private socket: WebSocket | null = null
  private intentionalClose = false
  private reconnectAttempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private readonly handlers = new Map<string, Set<WsMessageHandler>>()
  private readonly anyHandlers = new Set<WsMessageHandler>()

  constructor(options: WsClientOptions = {}) {
    this.url =
      (options.url ?? import.meta.env.VITE_WS_URL ?? DEFAULT_WS_URL).trim() || DEFAULT_WS_URL
    this.getToken = options.getToken ?? (() => localStorage.getItem('token'))
    this.role = options.role ?? 'admin'
    this.getTaskType = options.getTaskType
    this.getTaskId = options.getTaskId
    this.getAuthPayloadCustom = options.getAuthPayload
    this.subscribeRooms = options.subscribeRooms ?? []
    this.heartbeatMs = options.heartbeatMs ?? 25_000
    this.maxReconnectMs = options.maxReconnectMs ?? 30_000
    this.onStatus = options.onStatus
  }

  get readyState(): number {
    return this.socket?.readyState ?? WebSocket.CLOSED
  }

  private setStatus(status: WsConnectionStatus): void {
    this.onStatus?.(status)
  }

  /** Build current auth frame (technician track or admin JWT). */
  resolveAuthPayload(): WsAuthPayload | null {
    if (this.getAuthPayloadCustom) {
      return this.getAuthPayloadCustom()
    }
    const token = this.getToken()?.trim()
    if (!token) return null
    const payload: WsAuthPayload = {
      type: 'auth',
      role: this.role,
      token,
    }
    const taskType = this.getTaskType?.()?.trim()
    const taskId = this.getTaskId?.()
    if (taskType) payload.task_type = taskType
    if (taskId !== undefined && taskId !== null && String(taskId).trim() !== '') {
      payload.task_id = taskId
    }
    return payload
  }

  connect(): void {
    this.intentionalClose = false
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return
    }

    const auth = this.resolveAuthPayload()
    if (!auth?.token) {
      this.setStatus('no-token')
      console.warn('[ws] skip connect: no auth token')
      return
    }

    // Auth is first-frame JSON after open; do not put token in URL.
    console.info('[ws] connecting…', this.url, {
      role: auth.role,
      task_type: auth.task_type,
      task_id: auth.task_id,
    })
    this.setStatus('connecting')

    let socket: WebSocket
    try {
      socket = new WebSocket(this.url)
    } catch (error) {
      console.error('[ws] failed to create WebSocket', error)
      this.setStatus('closed')
      this.scheduleReconnect()
      return
    }

    this.socket = socket

    socket.addEventListener('open', () => {
      this.reconnectAttempt = 0
      this.setStatus('open')
      this.sendAuth()
      if (this.subscribeRooms.length) {
        this.send({ type: 'subscribe', rooms: this.subscribeRooms })
      }
      this.startHeartbeat()
      console.info('[ws] connected + auth sent', this.url)
    })

    socket.addEventListener('message', (event) => {
      console.info('[ws] message', event.data)
      const message = normalizeIncoming(event.data)
      if (!message) return
      if (message.type === 'ping') {
        this.send({ type: 'pong' })
        return
      }
      if (message.type === 'pong') return
      this.emit(message)
    })

    socket.addEventListener('close', (event) => {
      this.stopHeartbeat()
      this.socket = null
      this.setStatus('closed')
      console.warn('[ws] closed', { code: event.code, reason: event.reason || '(empty)' })
      if (!this.intentionalClose) this.scheduleReconnect()
    })

    socket.addEventListener('error', () => {
      console.warn('[ws] socket error (see Network → WS, not Wasm)')
    })
  }

  /** Re-send auth on an open socket (e.g. switch technician / task tracking). */
  sendAuth(): boolean {
    const auth = this.resolveAuthPayload()
    if (!auth?.token) {
      console.warn('[ws] sendAuth skipped: no auth payload')
      return false
    }
    this.send(auth)
    return true
  }

  disconnect(): void {
    this.intentionalClose = true
    this.clearReconnectTimer()
    this.stopHeartbeat()
    if (this.socket) {
      try {
        this.socket.close()
      } catch {
        /* ignore */
      }
      this.socket = null
    }
    this.setStatus('closed')
  }

  on(type: string, handler: WsMessageHandler): () => void {
    let set = this.handlers.get(type)
    if (!set) {
      set = new Set()
      this.handlers.set(type, set)
    }
    set.add(handler)
    return () => {
      set!.delete(handler)
      if (!set!.size) this.handlers.delete(type)
    }
  }

  onAny(handler: WsMessageHandler): () => void {
    this.anyHandlers.add(handler)
    return () => {
      this.anyHandlers.delete(handler)
    }
  }

  send(payload: unknown): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return
    const data = typeof payload === 'string' ? payload : JSON.stringify(payload)
    try {
      this.socket.send(data)
    } catch (error) {
      console.warn('[ws] send failed', error)
    }
  }

  private emit(message: WsIncomingMessage): void {
    this.anyHandlers.forEach((handler) => {
      try {
        handler(message)
      } catch (error) {
        console.error('[ws] handler error', error)
      }
    })
    const set = this.handlers.get(message.type)
    set?.forEach((handler) => {
      try {
        handler(message)
      } catch (error) {
        console.error('[ws] handler error', error)
      }
    })
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' })
    }, this.heartbeatMs)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer()
    const delay = Math.min(1000 * 2 ** this.reconnectAttempt, this.maxReconnectMs)
    this.reconnectAttempt += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (!this.intentionalClose) this.connect()
    }, delay)
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}
